<template>
  <div class="address-list">
    <h2>我的地址</h2>
    
    <div v-if="loading" class="loading">
      <p>加载中...</p>
    </div>
    
    <div v-else>
      <div class="address-header">
        <button class="btn btn-primary" @click="showAddForm" :disabled="addressCount >= maxCount">
          新增地址
        </button>
        <span class="address-count" v-if="!loading">
          已添加 {{ addressCount }}/{{ maxCount }} 个地址
        </span>
      </div>
      
      <div v-if="addresses.length === 0" class="empty-address">
        <p>暂无收货地址，请添加</p>
      </div>
      
      <div v-else class="address-items">
        <div v-for="address in addresses" :key="address.id" class="address-item card">
          <div class="address-content">
            <div class="address-header-row">
              <span class="receiver-name">{{ address.receiverName }}</span>
              <span class="phone">{{ address.phone }}</span>
              <span v-if="address.isDefault" class="default-badge">默认</span>
            </div>
            <div class="address-detail">
              {{ address.province }} {{ address.city }} {{ address.district }} {{ address.detailAddress }}
            </div>
            <div class="postal-code" v-if="address.postalCode">
              邮编：{{ address.postalCode }}
            </div>
          </div>
          <div class="address-actions">
            <button 
              class="btn btn-sm" 
              @click="setDefault(address)" 
              :disabled="address.isDefault"
              :class="{ 'btn-default': !address.isDefault, 'btn-default-active': address.isDefault }"
            >
              {{ address.isDefault ? '已默认' : '设为默认' }}
            </button>
            <button class="btn btn-sm btn-edit" @click="editAddress(address)">编辑</button>
            <button class="btn btn-sm btn-danger" @click="confirmDelete(address)">删除</button>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="showForm" class="modal-overlay">
      <div class="modal-content card">
        <AddressForm 
          :address="editingAddress" 
          :is-edit="isEditing"
          @save="handleSave"
          @cancel="closeForm"
        />
      </div>
    </div>
    
    <!-- 撤销提示条 -->
    <div v-if="showUndoToast" class="undo-toast">
      <span>地址已删除</span>
      <button class="undo-btn" @click="undoRemove">撤销</button>
    </div>
  </div>
</template>

<script>
import AddressForm from './AddressForm.vue'

export default {
  name: 'AddressList',
  components: {
    AddressForm
  },
  data() {
    return {
      addresses: [],
      addressCount: 0,
      maxCount: 15,
      loading: true,
      showForm: false,
      editingAddress: null,
      isEditing: false,
      // 撤销删除功能
      deletedAddresses: [],
      showUndoToast: false,
      undoTimer: null
    }
  },
  created() {
    this.checkLogin()
    this.fetchAddresses()
  },
  methods: {
    checkLogin() {
      const token = localStorage.getItem('customerToken')
      if (!token) {
        this.$router.push('/login')
        return
      }
    },
    
    async fetchAddresses() {
      try {
        this.loading = true
        const response = await this.$axios.get('/addresses', {
          headers: {
            'X-Username': localStorage.getItem('customerUsername')
          }
        })
        
        if (response.data.success) {
          this.addresses = response.data.addresses || []
          this.addressCount = response.data.count || 0
          this.maxCount = response.data.maxCount || 15
        }
      } catch (error) {
        console.error('获取地址列表失败:', error)
        if (error.response && error.response.status === 401) {
          this.$router.push('/login')
        }
      } finally {
        this.loading = false
      }
    },
    
    showAddForm() {
      if (this.addressCount >= this.maxCount) {
        alert(`最多只能添加${this.maxCount}个地址`)
        return
      }
      this.editingAddress = null
      this.isEditing = false
      this.showForm = true
    },
    
    editAddress(address) {
      this.editingAddress = { ...address }
      this.isEditing = true
      this.showForm = true
    },
    
    closeForm() {
      this.showForm = false
      this.editingAddress = null
      this.isEditing = false
    },
    
    async handleSave(addressData) {
      try {
        const headers = {
          'X-Username': localStorage.getItem('customerUsername')
        }
        
        let response
        if (this.isEditing && this.editingAddress) {
          response = await this.$axios.put(`/addresses/${this.editingAddress.id}`, addressData, { headers })
        } else {
          response = await this.$axios.post('/addresses', addressData, { headers })
        }
        
        if (response.data.success) {
          this.closeForm()
          this.fetchAddresses()
        } else {
          alert(response.data.message || '操作失败')
        }
      } catch (error) {
        console.error('保存地址失败:', error)
        alert(error.response?.data?.message || '保存失败，请重试')
      }
    },
    
    async setDefault(address) {
      if (address.isDefault) return
      
      try {
        const response = await this.$axios.put(`/addresses/${address.id}/default`, null, {
          headers: {
            'X-Username': localStorage.getItem('customerUsername')
          }
        })
        
        if (response.data.success) {
          this.fetchAddresses()
        } else {
          alert(response.data.message || '设置默认地址失败')
        }
      } catch (error) {
        console.error('设置默认地址失败:', error)
        alert(error.response?.data?.message || '设置失败，请重试')
      }
    },
    
    confirmDelete(address) {
      let message = `确定要删除地址"${address.receiverName} - ${address.province}${address.city}${address.district}"吗？`
      if (address.isDefault) {
        message += '\n\n注意：删除后该地址的默认状态将被取消，系统将自动设置其他地址为默认。'
      }
      
      if (confirm(message)) {
        this.deleteAddress(address.id)
      }
    },
    
    async deleteAddress(addressId) {
      try {
        const addressToRemove = this.addresses.find(a => a.id === addressId)
        if (!addressToRemove) return
        
        const response = await this.$axios.delete(`/addresses/${addressId}`, {
          headers: {
            'X-Username': localStorage.getItem('customerUsername')
          }
        })
        
        if (response.data.success) {
          this.addresses = this.addresses.filter(a => a.id !== addressId)
          this.addressCount = this.addresses.length
          
          // 保存删除的地址用于撤销
          this.deletedAddresses.push(addressToRemove)
          this.showUndoToast = true
          
          // 清除之前的定时器
          if (this.undoTimer) {
            clearTimeout(this.undoTimer)
          }
          
          // 3秒后隐藏撤销提示
          this.undoTimer = setTimeout(() => {
            this.showUndoToast = false
            this.deletedAddresses = []
          }, 3000)
        } else {
          alert(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error('删除地址失败:', error)
        alert(error.response?.data?.message || '删除失败，请重试')
      }
    },
    
    async undoRemove() {
      if (this.deletedAddresses.length === 0) return
      
      try {
        const headers = {
          'X-Username': localStorage.getItem('customerUsername')
        }
        
        // 重新添加所有删除的地址
        for (const address of this.deletedAddresses) {
          await this.$axios.post('/addresses', address, { headers })
        }
        
        // 重新获取地址列表
        await this.fetchAddresses()
        
        // 清除撤销状态
        this.showUndoToast = false
        this.deletedAddresses = []
        if (this.undoTimer) {
          clearTimeout(this.undoTimer)
          this.undoTimer = null
        }
      } catch (error) {
        console.error('撤销删除失败:', error)
        alert('撤销删除失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.address-list {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.address-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.address-count {
  color: #666;
  font-size: 14px;
}

.empty-address {
  text-align: center;
  padding: 40px;
  color: #999;
}

.address-items {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.address-item {
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.address-content {
  flex: 1;
}

.address-header-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.receiver-name {
  font-weight: bold;
  font-size: 16px;
}

.phone {
  color: #666;
}

.default-badge {
  background-color: #ff4757;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  border: 1px solid #ff4757;
}

.address-detail {
  color: #333;
  margin-bottom: 5px;
  line-height: 1.5;
}

.postal-code {
  color: #999;
  font-size: 13px;
}

.address-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-sm {
  padding: 5px 12px;
  font-size: 13px;
}

.btn-default {
  background-color: #4CAF50;
  color: white;
}

.btn-default:hover:not(:disabled) {
  background-color: #45a049;
}

.btn-default-active {
  background-color: #ff4757;
  color: white;
  cursor: default;
}

.btn-edit {
  background-color: #2196F3;
  color: white;
}

.btn-edit:hover {
  background-color: #1976D2;
}

.btn-danger {
  background-color: #f44336;
  color: white;
}

.btn-danger:hover {
  background-color: #d32f2f;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 20px;
}

.loading {
  text-align: center;
  padding: 40px;
}

/* 撤销提示样式 */
.undo-toast {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  padding: 14px 24px;
  background-color: #333;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  z-index: 2000;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: undoToastSlideIn 0.3s ease;
}

@keyframes undoToastSlideIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.undo-btn {
  padding: 6px 16px;
  background-color: #ff4400;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.undo-btn:hover {
  background-color: #ff6a00;
}

.address-list {
  color: var(--color-text);
}

.address-header,
.address-item,
.modal-content,
.undo-toast {
  border: 1px solid rgba(255, 255, 255, 0.72);
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: var(--shadow-soft);
}

.address-header,
.address-item {
  border-radius: 26px;
}

.address-header {
  padding: 1.2rem 1.35rem;
}

.address-item {
  transition:
    transform var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);
}

.address-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-medium);
}

.default-badge,
.btn-sm,
.undo-btn {
  border-radius: 999px;
}

.modal-content {
  border-radius: 28px;
}
</style>
